import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, CardBody, CardTitle,
  Input, InputGroup, Button, Table, Badge,
  Form, FormGroup, Label, Spinner, Alert,
  UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem
} from 'reactstrap';
import { Search, Filter, Download, Calendar, DollarSign, Globe, Star, ChevronLeft, ChevronRight, ShoppingCart } from 'react-feather';
import API from '../utils/api';

const Domains = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Filters - Set default to show domains expiring today and future
  const today = new Date().toISOString().split('T')[0];
  const [search, setSearch] = useState('');
  const [tld, setTld] = useState('');
  const [dropDateFrom, setDropDateFrom] = useState(today);
  const [dropDateTo, setDropDateTo] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [charType, setCharType] = useState('');
  const [domainLength, setDomainLength] = useState('');
  const [sortBy, setSortBy] = useState('drop_date');
  const [sortOrder, setSortOrder] = useState('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Watchlist
  const [watchlist, setWatchlist] = useState([]);

  // TLD list for filter
  const [tldList, setTldList] = useState([]);
  const [tldCacheTime, setTldCacheTime] = useState(null);

  // Enrichment data sources
  const [enrichmentSources, setEnrichmentSources] = useState([]);
  const [availableDataSources, setAvailableDataSources] = useState([]);
  const [selectedDataSources, setSelectedDataSources] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('selectedDataSources');
    return saved ? JSON.parse(saved) : [];
  });

  // Selected date/price variations for each domain
  const [selectedVariations, setSelectedVariations] = useState({});

  // Order modal
  const [orderModal, setOrderModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [eppCategories, setEppCategories] = useState([]);
  const [eppAccounts, setEppAccounts] = useState([]);
  const [orderData, setOrderData] = useState({
    epp_registrar_category_id: '',
    epp_account_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchDomains();
    fetchEppCategories();
    fetchAvailableDataSources();
  }, [currentPage, perPage, sortBy, sortOrder]);

  const fetchAvailableDataSources = async () => {
    try {
      const response = await API.get('/data-sources/');
      setAvailableDataSources(response.data.sources || []);
    } catch (err) {
      console.error('Failed to load data sources:', err);
    }
  };

  // Separate useEffect for stats that includes filter dependencies
  useEffect(() => {
    fetchStats();
  }, [search, tld, dropDateFrom, dropDateTo, minPrice, maxPrice]);

  // Separate useEffect for TLD list (runs only once or when cache expires)
  useEffect(() => {
    fetchTldList();
  }, []);

  const fetchDomains = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (search) params.append('search', search);
      if (tld) params.append('tld', tld);
      if (dropDateFrom) params.append('drop_date_from', dropDateFrom);
      if (dropDateTo) params.append('drop_date_to', dropDateTo);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      if (charType) params.append('char_type', charType);
      if (domainLength) params.append('domain_length', domainLength);

      const response = await API.get(`/domains?${params}`);
      setDomains(response.data.domains || []);
      setTotalPages(response.data.pagination?.pages || 0);
      setTotalCount(response.data.pagination?.total || 0);
      setEnrichmentSources(response.data.enrichment_sources || []);
      // Don't overwrite stats - they are managed separately by fetchStats()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch domains');
      console.error('Error fetching domains:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (currentFilters = null) => {
    try {
      // Use provided filters or current state
      const filters = currentFilters || {
        search,
        tld,
        drop_date_from: dropDateFrom,
        drop_date_to: dropDateTo,
        min_price: minPrice,
        max_price: maxPrice,
        char_type: charType,
        domain_length: domainLength
      };

      // Build query params - only add filters that are meaningfully set
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.tld) params.append('tld', filters.tld);

      // Only add date filters if they differ from default behavior
      // drop_date_from = today is the default, so only add it if drop_date_to is also set
      if (filters.drop_date_from && filters.drop_date_to) {
        params.append('drop_date_from', filters.drop_date_from);
        params.append('drop_date_to', filters.drop_date_to);
      } else if (filters.drop_date_from && filters.drop_date_from !== today) {
        params.append('drop_date_from', filters.drop_date_from);
      } else if (filters.drop_date_to) {
        params.append('drop_date_to', filters.drop_date_to);
      }

      if (filters.min_price !== null && filters.min_price !== '') params.append('min_price', filters.min_price);
      if (filters.max_price !== null && filters.max_price !== '') params.append('max_price', filters.max_price);
      if (filters.char_type) params.append('char_type', filters.char_type);
      if (filters.domain_length) params.append('domain_length', filters.domain_length);

      const queryString = params.toString();
      const url = queryString ? `/domains/stats?${queryString}` : '/domains/stats';

      console.log('Fetching stats with URL:', url);
      const response = await API.get(url);
      console.log('Stats response:', response.data);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchTldList = async () => {
    try {
      // Check if we have cached data less than 1 hour old
      const now = Date.now();
      if (tldCacheTime && (now - tldCacheTime) < 3600000) { // 1 hour = 3600000ms
        return; // Use cached data
      }

      const response = await API.get('/domains/tlds');
      setTldList(response.data.tlds || []);
      setTldCacheTime(now);
    } catch (err) {
      console.error('Error fetching TLD list:', err);
      // Fallback to common TLDs if API fails
      setTldList(['com', 'net', 'org', 'info', 'biz', 'us', 'co.uk', 'de']);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchDomains();
  };

  const handleClearFilters = () => {
    setSearch('');
    setTld('');
    setDropDateFrom(today);
    setDropDateTo('');
    setMinPrice('');
    setMaxPrice('');
    setCharType('');
    setDomainLength('');
    setCurrentPage(1);

    // Stats will be refreshed automatically by useEffect when state changes

    setTimeout(() => fetchDomains(), 100);
  };

  const handleSetTodayFilter = () => {
    // Clear other filters and set date filter to today only
    setSearch('');
    setTld('');
    setDropDateFrom(today);
    setDropDateTo(today);
    setMinPrice('');
    setMaxPrice('');
    setCharType('');
    setDomainLength('');
    setCurrentPage(1);

    // Trigger immediate data refresh
    setTimeout(() => {
      fetchDomains();
    }, 100);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const response = await API.post('/domains/export', {
        filters: {
          domain: search,
          tld,
          drop_date_from: dropDateFrom,
          drop_date_to: dropDateTo,
          min_price: minPrice,
          max_price: maxPrice,
          char_type: charType,
          domain_length: domainLength
        },
        format
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `domains_${new Date().toISOString().slice(0, 10)}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export domains');
      console.error('Export error:', err);
    }
  };

  const toggleWatchlist = async (domain) => {
    try {
      if (watchlist.includes(domain)) {
        await API.delete(`/domains/watchlist/${domain}`);
        setWatchlist(watchlist.filter(d => d !== domain));
      } else {
        await API.post('/domains/watchlist', { domain });
        setWatchlist([...watchlist, domain]);
      }
    } catch (err) {
      console.error('Watchlist error:', err);
    }
  };

  const formatDropTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return 'Soon';
    }
  };

  const fetchEppCategories = async () => {
    try {
      const response = await API.get('/epp/categories');
      setEppCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching EPP categories:', err);
    }
  };

  const fetchEppAccounts = async (categoryId) => {
    if (!categoryId) return;
    try {
      const response = await API.get(`/epp/accounts?category_id=${categoryId}`);
      setEppAccounts(response.data.accounts || []);
    } catch (err) {
      console.error('Error fetching EPP accounts:', err);
    }
  };

  const openOrderModal = (domain, variation) => {
    setSelectedDomain({ ...domain, selectedVariation: variation });
    setOrderData({
      epp_registrar_category_id: '',
      epp_account_id: '',
      notes: ''
    });
    setEppAccounts([]);
    setOrderModal(true);
  };

  const closeOrderModal = () => {
    setOrderModal(false);
    setSelectedDomain(null);
    setOrderData({
      epp_registrar_category_id: '',
      epp_account_id: '',
      notes: ''
    });
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    try {
      const orderPayload = {
        domain: selectedDomain.domain,
        drop_date: selectedDomain.selectedVariation.drop_date,
        price: selectedDomain.selectedVariation.price,
        epp_registrar_category_id: orderData.epp_registrar_category_id,
        epp_account_id: orderData.epp_account_id,
        notes: orderData.notes
      };

      const response = await API.post('/domains/order', orderPayload);

      if (response.data.success) {
        alert(`Order created successfully! Order ID: ${response.data.order_id}`);
        closeOrderModal();
      }
    } catch (err) {
      console.error('Order creation failed:', err);
      alert(err.response?.data?.error || 'Failed to create order');
    }
  };

  const handleOrderDataChange = (e) => {
    const { name, value } = e.target;
    setOrderData(prev => ({
      ...prev,
      [name]: value
    }));

    // Fetch EPP accounts when category changes
    if (name === 'epp_registrar_category_id' && value) {
      fetchEppAccounts(value);
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="page-heading">
            <Globe className="page-heading-icon" />
            Domain Catalog
          </h2>
          <small className="text-muted">Browse and order expiring domains</small>
        </Col>
      </Row>

      {/* Stats Cards */}
      {stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="bg-dark border-secondary">
              <CardBody>
                <div className="d-flex align-items-center">
                  <Globe className="text-primary me-3" size={32} />
                  <div>
                    <h6 className="text-muted mb-0">Total Domains</h6>
                    <h3 className="mb-0">{totalCount.toLocaleString()}</h3>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="bg-dark border-secondary">
              <CardBody>
                <div className="d-flex align-items-center">
                  <Calendar className="text-warning me-3" size={32} />
                  <div>
                    <h6 className="text-muted mb-0">Dropping Today</h6>
                    <h3
                      className="mb-0 text-warning"
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => handleSetTodayFilter()}
                      title="Click to view domains dropping today"
                    >
                      {(stats?.general?.releasing_today || 0).toLocaleString()}
                    </h3>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="bg-dark border-secondary">
              <CardBody>
                <div className="d-flex align-items-center">
                  <DollarSign className="text-success me-3" size={32} />
                  <div>
                    <h6 className="text-muted mb-0">Avg Price</h6>
                    <h3 className="mb-0">${stats.avg_price?.toFixed(2) || '0'}</h3>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="bg-dark border-secondary">
              <CardBody>
                <div className="d-flex align-items-center">
                  <Star className="text-info me-3" size={32} />
                  <div>
                    <h6 className="text-muted mb-0">This Week</h6>
                    <h3 className="mb-0">{stats.expiring_week?.toLocaleString() || 0}</h3>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="bg-dark border-secondary mb-4">
        <CardBody>
          <Form onSubmit={handleSearch}>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label className="text-white">Search Domain</Label>
                  <InputGroup>
                    <Input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Enter domain name..."
                      className="bg-dark text-white"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label className="text-white">TLD</Label>
                  <Input
                    type="select"
                    value={tld}
                    onChange={(e) => setTld(e.target.value)}
                    className="bg-dark text-white"
                  >
                    <option value="">All TLDs</option>
                    {tldList.map(tldName => (
                      <option key={tldName} value={tldName}>
                        .{tldName}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label className="text-white">Char Type</Label>
                  <Input
                    type="select"
                    value={charType}
                    onChange={(e) => setCharType(e.target.value)}
                    className="bg-dark text-white"
                  >
                    <option value="">All Characters</option>
                    <option value="alpha">Alpha Only (a-z)</option>
                    <option value="alphanum">Alphanumeric (a-z,0-9)</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label className="text-white">Length</Label>
                  <Input
                    type="select"
                    value={domainLength}
                    onChange={(e) => setDomainLength(e.target.value)}
                    className="bg-dark text-white"
                  >
                    <option value="">All Lengths</option>
                    <option value="1">1 char</option>
                    <option value="2">2 chars</option>
                    <option value="3">3 chars</option>
                    <option value="4">4 chars</option>
                    <option value="5">5 chars</option>
                    <option value="6-8">6-8 chars</option>
                    <option value="9-10">9-10 chars</option>
                    <option value="10-15">10-15 chars</option>
                    <option value="15+">15+ chars</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <FormGroup>
                  <Label className="text-white">Drop From</Label>
                  <Input
                    type="date"
                    value={dropDateFrom}
                    onChange={(e) => setDropDateFrom(e.target.value)}
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label className="text-white">Drop To</Label>
                  <Input
                    type="date"
                    value={dropDateTo}
                    onChange={(e) => setDropDateTo(e.target.value)}
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">Price Range</Label>
                  <div className="d-flex">
                    <Input
                      type="number"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      placeholder="Min"
                      className="bg-dark text-white me-1"
                    />
                    <Input
                      type="number"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      placeholder="Max"
                      className="bg-dark text-white"
                    />
                  </div>
                </FormGroup>
              </Col>
            </Row>

            {/* Data Sources Selector */}
            <Row className="mt-3">
              <Col md={12}>
                <FormGroup>
                  <Label className="text-white">Data Sources (select to show enrichment data)</Label>
                  <div className="d-flex flex-wrap gap-2">
                    {availableDataSources.map(source => (
                      <div key={source.id} className="form-check form-check-inline">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`ds-${source.id}`}
                          checked={selectedDataSources.includes(source.id)}
                          onChange={(e) => {
                            const newSelection = e.target.checked
                              ? [...selectedDataSources, source.id]
                              : selectedDataSources.filter(id => id !== source.id);
                            setSelectedDataSources(newSelection);
                            localStorage.setItem('selectedDataSources', JSON.stringify(newSelection));
                          }}
                        />
                        <label className="form-check-label text-white" htmlFor={`ds-${source.id}`}>
                          {source.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-between">
              <div>
                <Button color="primary" type="submit" className="me-2">
                  <Search size={16} className="me-1" />
                  Search
                </Button>
                <Button color="secondary" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </div>
              <UncontrolledDropdown>
                <DropdownToggle color="success">
                  <Download size={16} className="me-1" />
                  Export
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem onClick={() => handleExport('csv')}>
                    Export as CSV
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </Form>
        </CardBody>
      </Card>

      {/* Domains Table */}
      <Card className="bg-dark border-secondary">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <CardTitle tag="h4" className="text-white mb-0">
              Domains ({totalCount.toLocaleString()})
            </CardTitle>
            <div className="d-flex align-items-center">
              <Label className="text-white mb-0 me-2">Show:</Label>
              <Input
                type="select"
                value={perPage}
                onChange={(e) => setPerPage(parseInt(e.target.value))}
                className="bg-dark text-white"
                style={{ width: '100px' }}
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
              </Input>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-2">Loading domains...</p>
            </div>
          ) : error ? (
            <Alert color="danger">{error}</Alert>
          ) : (
            <>
              <Table dark hover responsive>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('domain')}>
                      Domain {sortBy === 'domain' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>TLD</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('chars_before_tld')}>
                      Chars {sortBy === 'chars_before_tld' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('drop_date')}>
                      Drop Date {sortBy === 'drop_date' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Time Left</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('registrar_price')}>
                      Price {sortBy === 'registrar_price' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    {selectedDataSources.length > 0 && enrichmentSources.map(source => {
                      if (!selectedDataSources.includes(source.id)) return null;

                      const columns = [];

                      // Main value column
                      columns.push(
                        <th key={`${source.id}_main`} style={{ cursor: 'pointer' }} onClick={() => handleSort(`ds${source.id}_main`)}>
                          {source.response_mapping?.main_value_label || source.name} {sortBy === `ds${source.id}_main` && (sortOrder === 'asc' ? '↑' : '↓')}
                        </th>
                      );

                      // Additional value columns
                      if (source.response_mapping?.additional_values) {
                        source.response_mapping.additional_values.forEach((val, idx) => {
                          const colKey = `ds${source.id}_${val.field.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
                          columns.push(
                            <th key={colKey} style={{ cursor: 'pointer' }} onClick={() => handleSort(colKey)}>
                              {val.label} {sortBy === colKey && (sortOrder === 'asc' ? '↑' : '↓')}
                            </th>
                          );
                        });
                      }

                      return columns;
                    })}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map((domain, idx) => {
                    const domainKey = `${domain.domain}_${idx}`;
                    const selectedIndex = selectedVariations[domainKey] || 0;
                    const selectedVariation = domain.date_price_variations && domain.date_price_variations[selectedIndex]
                      ? domain.date_price_variations[selectedIndex]
                      : { drop_date: domain.selected_drop_date, price: domain.selected_price };

                    return (
                      <tr key={idx}>
                        <td className="font-weight-bold">{domain.domain}</td>
                        <td>
                          <Badge className="bg-dark text-white border border-secondary">.{domain.tld}</Badge>
                        </td>
                        <td>
                          <Badge className="bg-dark text-info border border-info">{domain.chars_before_tld || '-'}</Badge>
                        </td>
                        <td>
                          {domain.date_price_variations && domain.date_price_variations.length > 1 ? (
                            <div className="d-flex align-items-center">
                              <select
                                className="form-control form-control-sm bg-dark text-white border-secondary"
                                value={selectedIndex}
                                onChange={(e) => setSelectedVariations(prev => ({
                                  ...prev,
                                  [domainKey]: parseInt(e.target.value)
                                }))}
                                style={{ width: 'auto', marginRight: '10px' }}
                              >
                                {domain.date_price_variations.map((variation, vIdx) => (
                                  <option key={vIdx} value={vIdx}>
                                    {variation.drop_date ?
                                      `${new Date(variation.drop_date).toLocaleDateString()} - $${variation.price || 0}` : 'N/A'
                                    }
                                  </option>
                                ))}
                              </select>
                              <Badge className="bg-dark text-info border border-info" pill>
                                {domain.variations_count} dates
                              </Badge>
                            </div>
                          ) : (
                            selectedVariation.drop_date ?
                              new Date(selectedVariation.drop_date).toLocaleDateString() : '-'
                          )}
                        </td>
                        <td>
                          <Badge className={formatDropTime(selectedVariation.drop_date) === 'Soon' ? 'bg-danger text-white' : 'bg-dark text-warning border border-warning'}>
                            {formatDropTime(selectedVariation.drop_date)}
                          </Badge>
                        </td>
                        <td>
                          <Badge className="bg-dark text-success border border-success">
                            ${selectedVariation.price || 0}
                          </Badge>
                        </td>
                        {selectedDataSources.length > 0 && enrichmentSources.map(source => {
                          if (!selectedDataSources.includes(source.id)) return null;

                          const cells = [];

                          // Main value cell
                          cells.push(
                            <td key={`${source.id}_main`}>
                              <Badge className="bg-dark text-info border border-info">
                                {domain.enrichment?.[`ds${source.id}_main`] || '-'}
                              </Badge>
                            </td>
                          );

                          // Additional value cells
                          if (source.response_mapping?.additional_values) {
                            source.response_mapping.additional_values.forEach((val, idx) => {
                              const colKey = `ds${source.id}_${val.field.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
                              cells.push(
                                <td key={colKey}>
                                  <small className="text-muted">
                                    {domain.enrichment?.[colKey] || '-'}
                                  </small>
                                </td>
                              );
                            });
                          }

                          return cells;
                        })}
                        <td>
                          <div className="btn-group btn-group-sm">
                            <Button
                              color="outline-primary"
                              size="sm"
                              onClick={() => openOrderModal(domain, selectedVariation)}
                              title="Order Domain"
                              className="me-1"
                            >
                              <ShoppingCart size={14} />
                            </Button>
                            <Button
                              color={watchlist.includes(domain.domain) ? 'warning' : 'outline-warning'}
                              size="sm"
                              onClick={() => toggleWatchlist(domain.domain)}
                              title="Add to Watchlist"
                            >
                              <Star size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link bg-dark border-secondary text-white"
                          onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft size={16} />
                        </button>
                      </li>

                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        const pageNum = currentPage <= 3
                          ? i + 1
                          : currentPage + i - 2;

                        if (pageNum > 0 && pageNum <= totalPages) {
                          return (
                            <li key={i} className={`page-item ${pageNum === currentPage ? 'active' : ''}`}>
                              <button
                                className={`page-link ${
                                  pageNum === currentPage
                                    ? 'bg-primary border-primary'
                                    : 'bg-dark border-secondary text-white'
                                }`}
                                onClick={() => setCurrentPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        }
                        return null;
                      })}

                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link bg-dark border-secondary text-white"
                          onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight size={16} />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Order Modal */}
      {orderModal && selectedDomain && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content bg-dark text-white">
              <div className="modal-header border-secondary">
                <h5 className="modal-title">
                  <ShoppingCart className="me-2" size={20} />
                  Order Domain: {selectedDomain.domain}
                </h5>
                <button type="button" className="btn-close-enhanced" onClick={closeOrderModal}>×</button>
              </div>

              <form onSubmit={handleOrderSubmit}>
                <div className="modal-body">
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Domain</Label>
                        <Input
                          type="text"
                          value={selectedDomain.domain}
                          className="bg-secondary border-secondary text-white"
                          disabled
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Drop Date</Label>
                        <Input
                          type="text"
                          value={new Date(selectedDomain.selectedVariation.drop_date).toLocaleDateString()}
                          className="bg-secondary border-secondary text-white"
                          disabled
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Price</Label>
                        <Input
                          type="text"
                          value={`$${selectedDomain.selectedVariation.price}`}
                          className="bg-secondary border-secondary text-white"
                          disabled
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>EPP Registrar Category <span className="text-danger">*</span></Label>
                        <Input
                          type="select"
                          name="epp_registrar_category_id"
                          value={orderData.epp_registrar_category_id}
                          onChange={handleOrderDataChange}
                          className="bg-secondary border-secondary text-white"
                          required
                        >
                          <option value="">Select category...</option>
                          {eppCategories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name} ({category.epp_server_url})
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {eppAccounts.length > 0 && (
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>EPP Account (Admin Only)</Label>
                          <Input
                            type="select"
                            name="epp_account_id"
                            value={orderData.epp_account_id}
                            onChange={handleOrderDataChange}
                            className="bg-secondary border-secondary text-white"
                          >
                            <option value="">Use default account...</option>
                            {eppAccounts.map(account => (
                              <option key={account.id} value={account.id}>
                                {account.name} ({account.username})
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  <FormGroup>
                    <Label>Notes</Label>
                    <Input
                      type="textarea"
                      name="notes"
                      value={orderData.notes}
                      onChange={handleOrderDataChange}
                      placeholder="Optional notes for this order..."
                      rows={3}
                      className="bg-secondary border-secondary text-white"
                    />
                  </FormGroup>
                </div>

                <div className="modal-footer border-secondary">
                  <Button color="secondary" onClick={closeOrderModal}>
                    Cancel
                  </Button>
                  <Button color="primary" type="submit">
                    Create Order
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default Domains;